import { Button, Form, Input, Modal, notification, Space } from "antd";
import Papa from "papaparse";
import { useRef, useState } from "react";
import { CSVUser } from "../Users.types";
import { appInject } from "@core/di/utils";
import { IUserService } from "@shared/interfaces/user.interface";
import { DI_TOKENS } from "@shared/constants/di";
import { useMutation, UseMutationResult } from "react-query";

interface AddTokensBulkModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddTokensBulkModal = ({
  open,
  onClose,
  onSuccess,
}: AddTokensBulkModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [converting, setConverting] = useState(false);
  const [filteredData, setFilteredData] = useState<CSVUser[]>([]);
  const [usersNotFound, setUsersNotFound] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const usersService = appInject<IUserService>(DI_TOKENS.userService);

  const giveTokensBulkMutation: UseMutationResult<
    Awaited<ReturnType<IUserService["addTokensBulk"]>>,
    unknown,
    Parameters<IUserService["addTokensBulk"]>[0],
    unknown
  > = useMutation((data) => usersService.addTokensBulk(data), {
    onSuccess: (data) => {
      const responseUsersNotFound = data?.data?.usersNotFound ?? [];

      if (
        Array.isArray(responseUsersNotFound) &&
        responseUsersNotFound.length > 0
      ) {
        setFilteredData([]);
        setSelectedFile(null);
        clearInput();
        setUsersNotFound(responseUsersNotFound);
      } else {
        onSuccess();
      }

      notification.success({
        message: data.message,
      });
    },
  });

  const clearInput = () => {
    setUsersNotFound([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (event: any) => {
    try {
      setFilteredData([]);

      let file = event.target.files?.[0] ?? null;

      clearInput();

      if (!file) {
        return;
      }

      setSelectedFile(file);
      setConverting(true);

      Papa.parse(file, {
        complete: (result: Papa.ParseResult<CSVUser>) => {
          const parsedUsers: CSVUser[] = [];
          const requiredColumns: (keyof CSVUser)[] = [
            "nickname",
            "draftTokensToGive",
          ];
          const actualColumns = Object.keys(result.data[0] || {});

          if (
            !requiredColumns.every((column) => actualColumns.includes(column))
          ) {
            notification.error({
              message:
                'CSV file must contain "nickname" and "draftTokensToGive" column.',
            });
            setConverting(false);
            return;
          }

          const lastElement = result.data[result.data.length - 1];

          if (lastElement.nickname === "") {
            result.data.splice(result.data.length - 1, 1);
          }

          for (const { nickname, draftTokensToGive } of result.data) {
            const trimmedNickname = nickname?.trim();
            const numericTokens = Number(draftTokensToGive);
            const isValid = trimmedNickname && Number.isFinite(numericTokens);

            if (isValid) {
              parsedUsers.push({
                nickname: trimmedNickname,
                draftTokensToGive: numericTokens,
              });
            } else {
              notification.info({
                message:
                  'Invalid CSV data. Please ensure all rows have valid "nickname" and "draftTokensToGive" values.',
              });
              setConverting(false);
              return;
            }
          }

          if (parsedUsers.length === 0) {
            notification.error({
              message: "No valid data found in the CSV file.",
            });
            setConverting(false);
            return;
          }

          setFilteredData(parsedUsers);
          setConverting(false);
        },
        error: (err) => {
          console.error("CSV parse error", err);
          notification.error({
            message: "Failed to parse CSV file.",
          });
          setConverting(false);
        },
        header: true,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleFinish = async () => {
    try {
      await giveTokensBulkMutation.mutateAsync(filteredData);
    } catch (error) {
      console.error(error);
      notification.error({
        message:
          "An error occurred while adding tokens. Please try again later.",
      });
    }
  };

  function formatUserCount() {
    const count = filteredData.length;
    return `${count} User${count === 1 ? "" : "s"}`;
  }

  return (
    <Modal
      title="Assign Bulk Tokens"
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <Form layout="vertical">
        <Form.Item label={"CSV File"}>
          <Input
            type="text"
            readOnly
            value={
              converting
                ? "Parsing..."
                : selectedFile
                  ? selectedFile.name
                  : "Choose file"
            }
            disabled={converting}
            style={{ width: "100%", cursor: converting ? "not-allowed" : "pointer" }}
            onClick={() => {
              if (!converting) fileInputRef.current?.click();
            }}
          />

          <input
            style={{
              display: "none",
            }}
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={converting}
          />
        </Form.Item>

        {usersNotFound.length ? (
          <Form.Item label="The following users were not found in the database:">
            {usersNotFound.join(", ")}
          </Form.Item>
        ) : null}
      </Form>

      <Space
        style={{
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <Button htmlType="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleFinish}
          type="primary"
          htmlType="button"
          disabled={
            filteredData.length === 0 ||
            giveTokensBulkMutation.isLoading ||
            converting
          }
        >
          {`Update ${formatUserCount()}`}
        </Button>
      </Space>
    </Modal>
  );
};

export default AddTokensBulkModal;
